/**
 * Definición completa de un template de NPC o Mob.
 * Esta es la fuente de verdad única para todos los templates de NPCs y Mobs del juego.
 * Los mobs son técnicamente NPCs con hostile: true (no hay diferencia en la implementación).
 * 
 * IMPORTANTE: Solo incluir campos que difieren de los valores por defecto.
 * Los valores por defecto se manejan en el servidor cuando se crea la instancia.
 * 
 * Valores por defecto (se aplican en applyTemplatePropertiesToNpc):
 * - bodyId: 1
 * - headId: 0
 * - weaponId: 0
 * - shieldId: 0
 * - helmId: 0
 * - interact: 0
 * - maxHp: 0
 * - attackable: false
 * - hostile: false
 * - respawnTime: 0
 * - sprite: undefined (opcional)
 * - dialogue: undefined (opcional)
 * - message: undefined (opcional)
 */
/**
 * Entrada de una tabla de loot. Cada entrada se rollea de forma INDEPENDIENTE
 * cuando el NPC muere: Math.random() < chance → cae una cantidad random entre
 * minQty y maxQty (inclusive). Pueden caer 0 a N items por kill.
 */
export interface NPCLootEntry {
  itemId: string;        // ID del template en shared/data/items.ts
  chance: number;        // Probabilidad 0..1 (1 = siempre cae)
  minQty: number;        // Cantidad mínima (inclusive)
  maxQty: number;        // Cantidad máxima (inclusive)
}

/**
 * Spell que el NPC puede lanzar en combate. El servidor reusa el SpellSystem
 * existente: spellId debe existir en shared/data/spells.ts.
 *
 * Prioridad determina el contexto en el que el AI considera el spell:
 *  - 'when_hp_below': cura/buff self cuando hp/maxHp < hpThreshold.
 *  - 'on_target':    spell ofensivo lanzado al target actual si está en rango.
 *  - 'periodic':     rotación cada periodMs ms (AoE u otros).
 *
 * El AI evalúa los spells del template EN ORDEN y elige el primero elegible
 * (condición cumplida y off-cooldown). cooldownOverride pisa el spell.cooldown
 * por si querés que un NPC particular spamme más rápido o lento.
 */
export interface NPCSpellEntry {
  spellId: string;
  priority: 'when_hp_below' | 'on_target' | 'periodic';
  hpThreshold?: number;     // 0..1, requerido para 'when_hp_below'
  periodMs?: number;        // ms, requerido para 'periodic'
  cooldownOverride?: number; // ms, opcional
}

/**
 * Terreno por el que puede moverse una criatura (ver docs/NPC_HOSTILE_AI.md §Traversal):
 *  - 'land'  (default): camina por tierra, el agua la bloquea (como todos los NPCs hoy).
 *  - 'water': sólo agua (peces/tiburones) — la TIERRA la bloquea.
 *  - 'both' : anfibia (tortugas/cangrejos) — ni tierra ni agua la bloquean.
 * Los tiles `blocked`/ocupados siguen bloqueando para todos.
 */
export type TraversalType = 'land' | 'water' | 'both';

export interface NPCTemplateDefinition {
  id: string;
  name: string;
  sprite?: string; // Referencia al sprite (opcional, por compatibilidad)
  dialogue?: string; // Diálogo por defecto del NPC
  // Apariencia básica del NPC (IDs visuales para el cliente)
  // Solo especificar si difiere del default
  bodyId?: number; // Default: 1
  headId?: number; // Default: 0
  weaponId?: number; // Default: 0
  shieldId?: number; // Default: 0
  helmId?: number; // Default: 0
  // Set de footstep (huella al caminar) que deja este NPC. Default: 1. Ver
  // FootstepSpriteManager (client) y docs/SPRITE_SYSTEM.md §Footsteps.
  footstepId?: number; // Default: 1
  // Interactividad: 1 = interactivo (cambia cursor), 0 = no interactivo
  interact?: number; // Default: 0
  // Mensaje que muestra el NPC al hacer click
  message?: string; // Default: undefined

  // =========================================================================
  // SONIDOS (opcionales; data-driven — ver docs/SOUND_AND_MUSIC.md)
  // =========================================================================
  // Sonidos propios del NPC: al saludar/interactuar, tradear, revivir, enseñar
  // hechizo, fallar al enseñar, morir, ser golpeado. La MAYORÍA de los NPCs NO
  // define ninguno: caen al default de su categoría (o silencio). Cada campo
  // apunta a un id de `SOUNDS` (shared/data/sounds.ts).
  sounds?: NpcSounds;

  // =========================================================================
  // STATS DE COMBATE (todos opcionales; defaults vienen de BaseEntity)
  // Aplican vía npc.applyBaseAttributes() en NPCManager.applyTemplatePropertiesToNpc.
  // Las defensas y la evasión los consume calculateDamage del atacante.
  // =========================================================================
  maxHp?: number;        // Default: 0 (inmortal/no atacable)
  minDamage?: number;    // Default: 1
  maxDamage?: number;    // Default: 5
  physDef?: number;      // Default: 0
  magicDef?: number;     // Default: 0
  evasion?: number;      // 0..1, default: 0
  attackSpeed?: number;  // Multiplicador, default: 1.0
  moveSpeed?: number;    // Multiplicador, default: 1.0

  // =========================================================================
  // FLAGS DE COMPORTAMIENTO
  // =========================================================================
  // Si el NPC puede ser atacado (si maxHp > 0, normalmente debería ser true)
  attackable?: boolean;  // Default: false
  // Si el NPC es hostil (mob). Los mobs son técnicamente NPCs pero con hostile = true
  hostile?: boolean;     // Default: false
  // Marca al NPC como "boss". Por ahora solo expone el flag al cliente (UI/marker)
  // y se reservará para lógica especial (loot único, broadcast global, etc.).
  isBoss?: boolean;      // Default: false
  // Rango de aggro/detección del mob. Si false (default), el mob ve players
  // dentro de un rect Chebyshev de VIEW_HALF + 10 tiles centrado en sí mismo
  // (es decir, te detecta antes de que aparezca en tu pantalla). Si true,
  // usa el mismo viewport que el player (te ve cuando vos lo ves).
  normalViewport?: boolean; // Default: false
  // INVOCACIONES: si true, cuando hay una invocación de un jugador dentro del
  // aggro range el mob la prioriza como objetivo por encima de los players. Si
  // false (default), elige simplemente al objetivo más cercano (players +
  // summons). Ver docs/SUMMONS.md.
  prioritizeSummons?: boolean; // Default: false
  // INVISIBILIDAD: si true, este mob VE a los jugadores invisibles (los agrede y
  // mantiene como target igual que a uno visible). Default false = un invisible le
  // pierde el aggro. Pensado para bestias con "olfato"/no-visuales. Ver docs/INVISIBILITY.md.
  seesInvisible?: boolean; // Default: false

  // =========================================================================
  // RESPAWN
  // =========================================================================
  // Si false, el mob NO respawnea al morir (one-shot, ej. spawns dinámicos).
  // Default: true cuando hostile=true; false en otros casos.
  respawn?: boolean;
  // Tiempo de respawn en milisegundos. Solo aplica si respawn=true.
  respawnTime?: number;  // Default: 0
  // Si true, al respawnear se broadcastea NPC_RESPAWNED a la sala (toast).
  // Stub para futuro broadcast global de bosses multi-sala.
  notifyOnRespawn?: boolean; // Default: false

  // =========================================================================
  // EXPERIENCIA
  // =========================================================================
  expReward?: number;    // Pool total de XP del mob. Default: 0
  // Si true, la XP se reparte por daño hecho (XP × dañoHecho / maxHp) al
  // INSTANTE de cada golpe, descontando del pool del mob. El player se queda
  // con lo cobrado aunque el mob no muera. Si false, todo el pool va al
  // último golpe. Default: false.
  expOnHit?: boolean;
  // Oro otorgado al matador al morir. Random entre min y max (inclusive).
  // Se suma directo al balance del player (no es item en el suelo).
  // Default: undefined (no dropea oro).
  goldReward?: { min: number; max: number };

  // =========================================================================
  // LOOT
  // =========================================================================
  // Tabla de drops. Al morir, cada entrada se rollea INDEPENDIENTEMENTE; los
  // items caen al suelo (un item por tile) usando findAvailableTile en
  // espiral desde el cadáver. Default: undefined (no dropea nada).
  lootTable?: NPCLootEntry[];

  // Si true, el loot de este bicho va DIRECTO al inventario del matador (o del
  // miembro asignado del party) en vez de caer al piso; el overflow (inventario
  // lleno) sí cae al piso cerca del cadáver. Default: undefined (cae al piso).
  // Ver docs/PARTY_SYSTEM.md §Loot.
  lootDirectToInventory?: boolean;

  // =========================================================================
  // SPELLS (AI de combate)
  // =========================================================================
  // Spells que el NPC puede lanzar. IDs del catálogo shared/data/spells.ts.
  // Default: undefined (mob solo hace melee si tiene target en rango).
  npcSpells?: NPCSpellEntry[];

  // =========================================================================
  // MERCHANT (vendor de items)
  // =========================================================================
  // ID de un MerchantDefinition en shared/data/merchants.ts. Si está set,
  // INTERACT_NPC sobre este NPC abre el MerchantPanel en vez del chat bubble.
  // Default: undefined (no vende nada).
  merchantId?: string;

  // =========================================================================
  // SPELL VENDOR (vendor de hechizos)
  // =========================================================================
  // ID de un SpellVendorDefinition en shared/data/spellVendors.ts. Si está set,
  // INTERACT_NPC sobre este NPC abre el SpellVendorPanel. Mutuamente excluyente
  // con merchantId en la práctica: si ambos están seteados, gana spellVendorId.
  // Default: undefined (no enseña spells).
  spellVendorId?: string;

  // =========================================================================
  // PRIEST (sacerdote: revive / heal / cleanse)
  // =========================================================================
  // Si true, INTERACT_NPC sobre este NPC dispara el handler de sacerdote en
  // GameRoom (handlePriestService). Prioridad de acciones: revive (si está
  // muerto) > cleanse (si tiene debuffs) > heal (si HP < max) > nothing.
  // Requiere distancia ≤ 3 tiles (Chebyshev). Default: undefined.
  // Si están seteados merchantId o spellVendorId, esos ganan en el dispatch.
  priestService?: boolean;

  // =========================================================================
  // CUSTODIO DE LA CASA DE SUBASTAS (Exchange — mercado de fungibles)
  // =========================================================================
  // Si true, INTERACT_NPC sobre este NPC abre la Casa de Subastas (Exchange):
  // libro de órdenes de fungibles, compra/venta, Cajón de Retiro. Requiere
  // distancia ≤ 2 tiles. Default: undefined. Mutuamente excluyente con vendors
  // en la práctica. Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
  marketCustodian?: boolean;

  // =========================================================================
  // PARTY FINDER (ver docs/PARTY_SYSTEM.md)
  // =========================================================================
  // Si true, INTERACT_NPC sobre este NPC abre el Party Finder: crear party solo,
  // togglear público/cerrado + tag de nivel, y unirse a parties públicas.
  // Default: undefined.
  partyFinder?: boolean;

  // =========================================================================
  // QUESTS (ver docs/QUESTS.md)
  // =========================================================================
  // IDs de QuestDefinition (shared/data/quests.ts) que este NPC OFRECE. El mismo
  // NPC también recibe la entrega de cualquier quest cuyo turnInNpcId (o giverNpcId)
  // apunte a él. INTERACT_NPC resuelve: completar objetivos 'talk' → entregar quest
  // ready → ofrecer quest disponible → diálogo normal. Default: undefined.
  questIds?: string[];

  // =========================================================================
  // FACCIONES (ver docs/FACTIONS.md)
  // =========================================================================
  // Facción a la que pertenece el NPC ('aresgard' | 'elvaria' | 'rebel'). Para:
  //  - vestimenta/identidad del NPC,
  //  - guardias: a quién targetear (enemigos de esta facción),
  //  - dueño implícito si el NPC está dentro de un área de su facción.
  // Default: undefined (NPC neutral sin facción).
  factionId?: string;
  // Si true, INTERACT_NPC ofrece unirse a `factionId` (solo a Newbies). El
  // server manda FACTION_JOIN_PROMPT y el cliente confirma. Default: undefined.
  factionRecruiter?: boolean;
  // Si true, INTERACT_NPC abre el panel de rango/ascenso del Rey Faccionario
  // (solo same-faction). Default: undefined.
  factionKing?: boolean;
  // Tipo de guardia faccionario. Si está set, el NPC es un guardia hostil que
  // solo agrede players enemigos de su `factionId` (ver NPCManager.findAggroTarget).
  //  - 'melee':  pega cuerpo a cuerpo.
  //  - 'caster': lanza spells (definir npcSpells).
  // Default: undefined (no es guardia).
  guardType?: 'melee' | 'caster';

  // =========================================================================
  // REINO DE CRIATURA (ver docs/CREATURE_KINGDOMS.md)
  // =========================================================================
  // Categoría taxonómica del NPC (animal, beast, humanoid, undead, construct, ...).
  // La consume la lógica de combate por tipo (ej. el modifier damageVsKingdom).
  // Default: 'humanoid' (DEFAULT_KINGDOM) si no se especifica.
  kingdom?: KingdomId;

  // =========================================================================
  // TRAVERSAL POR TERRENO (ver docs/NPC_HOSTILE_AI.md §Traversal)
  // =========================================================================
  // Terreno por el que se mueve la criatura: 'land' (default) / 'water' / 'both'.
  // Gatea el pathfinder y el random-walk (NpcAiController). Default 'land'.
  traversal?: TraversalType;
}

/**
 * Índice completo de templates de NPCs y Mobs por id.
 * Esta es la fuente de verdad única: todos los templates de NPCs y Mobs deben estar definidos aquí.
 * Los mapas referencian estos templates por id, y cuando se instancia un mapa,
 * se crean instancias de NPCs basadas en estos templates (mobs son NPCs con hostile: true).
 * 
 * ESTRUCTURA:
 * - Los NPCs amigables usan prefijo "npc_"
 * - Los NPCs hostiles (mobs) usan prefijo "hostile_"
 * - Solo incluir campos que difieren de los valores por defecto (ver NPCTemplateDefinition)
 * 
 * CAMBIOS:
 * - [Fecha] - Agregado campo 'hostile' para distinguir mobs de NPCs amigables
 * - [Fecha] - Cambiada estructura para solo incluir campos no-default (valores default se manejan en servidor)
 */
export const NPC_TEMPLATES: Record<string, NPCTemplateDefinition> = {
  npc_001: {
    id: 'npc_001',
    name: 'Guardián del Pueblo',
    sprite: 'guardian',
    dialogue: 'Bienvenido, aventurero. ¿Necesitas ayuda?',
    headId: 1,
    weaponId: 1,
    shieldId: 1,
    interact: 1,
    message: 'Bienvenido, aventurero. ¿Necesitas ayuda?',
  },
  // NPC dedicado a quests (ver docs/QUESTS.md). Ofrece quest_intro_hunt.
  npc_quest_1: {
    id: 'npc_quest_1',
    name: 'Capitán Aldric',
    sprite: 'guardian',
    dialogue: '¿Buscás trabajo, aventurero?',
    headId: 1,
    weaponId: 1,
    interact: 1,
    message: '¿Buscás trabajo, aventurero? Tengo una misión para vos.',
    questIds: ['quest_intro_hunt'],
  },
  npc_002: {
    id: 'npc_002',
    name: 'Comerciante',
    sprite: 'merchant',
    dialogue: '¡Tengo los mejores precios de la ciudad!',
    headId: 1,
    interact: 1,
    message: '¡Tengo los mejores precios de la ciudad!',
    // merchantId removido en wipe 2026-05-25 (potions merchant ya no existe).
    // Queda como NPC genérico hasta que le asignemos un nuevo merchant.
  },
  npc_vendor_starter_weapons: {
    id: 'npc_vendor_starter_weapons',
    name: 'Armero',
    sprite: 'merchant',
    dialogue: 'Tengo armas de iniciación para todo aspirante a aventurero.',
    headId: 1,
    interact: 1,
    message: 'Tengo armas de iniciación para todo aspirante a aventurero.',
    merchantId: 'merchant_starter_weapons',
  },
  npc_vendor_starter_armors: {
    id: 'npc_vendor_starter_armors',
    name: 'Sastre',
    sprite: 'merchant',
    dialogue: 'Tengo vestimentas de iniciación para protegerte en tus primeros pasos.',
    headId: 1,
    interact: 1,
    message: 'Tengo vestimentas de iniciación para protegerte en tus primeros pasos.',
    merchantId: 'merchant_starter_armors',
  },
  npc_vendor_smith_collection: {
    id: 'npc_vendor_smith_collection',
    name: 'Maestro Herrero',
    sprite: 'merchant',
    dialogue: 'Del bronce al dragón: aquí tienes toda la colección que un herrero puede forjar.',
    headId: 1,
    interact: 1,
    message: 'Del bronce al dragón: aquí tienes toda la colección que un herrero puede forjar.',
    merchantId: 'merchant_smith_collection',
  },
  npc_priest_starter: {
    id: 'npc_priest_starter',
    name: 'Sacerdote',
    sprite: 'mentor',
    dialogue: 'Acércate, te puedo sanar o resucitar.',
    headId: 1,
    interact: 1,
    message: 'Acércate (click derecho) para curarte, purificarte o resucitar.',
    priestService: true,
  },
  npc_market_custodian: {
    id: 'npc_market_custodian',
    name: 'Custodio',
    sprite: 'merchant',
    dialogue: 'Bienvenido a la Casa de Subastas. Aquí se comercian materiales y enseres.',
    headId: 1,
    interact: 1,
    message: 'Acércate (click derecho) para operar en la Casa de Subastas.',
    marketCustodian: true,
  },
  npc_money_changer: {
    id: 'npc_money_changer',
    name: 'Cambista',
    sprite: 'merchant',
    dialogue: 'Sellos de Aureum a la venta. Prestigio acuñado en oro.',
    headId: 1,
    interact: 1,
    message: 'Acércate (click derecho) para comprar Sellos de Aureum.',
    merchantId: 'merchant_bonds',
  },
  npc_party_finder: {
    id: 'npc_party_finder',
    name: 'Heraldo de Grupos',
    sprite: 'mentor',
    dialogue: '¿Buscás compañeros de aventura? Yo conecto héroes.',
    headId: 1,
    interact: 1,
    message: 'Acércate (click derecho) para armar o buscar un grupo.',
    partyFinder: true,
  },
  npc_003: {
    id: 'npc_003',
    name: 'Mentor',
    sprite: 'mentor',
    dialogue: 'Puedo enseñarte las artes del combate.',
    headId: 1,
    weaponId: 1,
    interact: 1,
    message: 'Puedo enseñarte las artes del combate.',
  },
  npc_004: {
    id: 'npc_004',
    name: 'NPC Grande',
    sprite: 'guardian',
    dialogue: 'Soy un NPC de prueba.',
    bodyId: 2,
    interact: 1,
    message: 'Me estas haciendo click',
  },
  // =========================================================================
  // SPELL VENDORS — NPCs no hostiles que enseñan spells a cambio de oro (+ items)
  // =========================================================================
  npc_spell_vendor_arcane: {
    id: 'npc_spell_vendor_arcane',
    name: 'Maestro Arcano',
    sprite: 'mentor',
    headId: 1,
    interact: 1,
    message: 'Domino las artes arcanas. ¿Querés aprender un hechizo?',
    spellVendorId: 'spell_vendor_arcane',
  },
  npc_spell_vendor_cleric: {
    id: 'npc_spell_vendor_cleric',
    name: 'Sacerdote Templario',
    sprite: 'mentor',
    headId: 1,
    interact: 1,
    message: 'Las bendiciones de la luz están a tu alcance.',
    spellVendorId: 'spell_vendor_cleric',
  },
  npc_spell_vendor_wild: {
    id: 'npc_spell_vendor_wild',
    name: 'Domador Salvaje',
    sprite: 'mentor',
    headId: 1,
    interact: 1,
    message: 'La naturaleza ofrece poder a quien sepa escucharla.',
    spellVendorId: 'spell_vendor_wild',
  },
  npc_spell_vendor_scholar: {
    id: 'npc_spell_vendor_scholar',
    name: 'Erudito Errante',
    sprite: 'mentor',
    headId: 1,
    interact: 1,
    message: 'Vendo conocimientos de muchas escuelas. ¿Qué buscás?',
    spellVendorId: 'spell_vendor_scholar',
  },

  // =========================================================================
  // FACCIONES — Reclutadores, Reyes y Guardias (ver docs/FACTIONS.md).
  // Apariencia placeholder hasta tener la ropa de cada facción.
  // TODO(faction-armor): vestir reclutadores/reyes/guardias con el armor de su
  // facción cuando exista el asset.
  // =========================================================================

  // --- Reclutadores: un Newbie hace click derecho para unirse a la facción ---
  npc_recruiter_aresgard: {
    id: 'npc_recruiter_aresgard',
    name: 'Reclutador de Aresgard',
    sprite: 'guardian',
    // Aresgard: heads de orco (más brutos). Diferenciación temporal por
    // head+body hasta tener la ropa de facción. TODO(faction-armor).
    bodyId: 2,
    headId: 41,
    weaponId: 1,
    interact: 1,
    message: '¡Aresgard necesita guerreros! ¿Te unís a nuestra causa?',
    factionId: 'aresgard',
    factionRecruiter: true,
  },
  npc_recruiter_elvaria: {
    id: 'npc_recruiter_elvaria',
    name: 'Reclutador de Elvaria',
    sprite: 'guardian',
    // Elvaria: heads de elfo (más refinados).
    bodyId: 2,
    headId: 21,
    weaponId: 1,
    interact: 1,
    message: 'Elvaria te abre sus puertas. ¿Lucharás por nosotros?',
    factionId: 'elvaria',
    factionRecruiter: true,
  },

  // --- Reyes: ascenso de rango + recompensas (solo miembros de la facción) ---
  npc_king_aresgard: {
    id: 'npc_king_aresgard',
    name: 'Rey de Aresgard',
    sprite: 'guardian',
    bodyId: 3,
    headId: 42,
    interact: 1,
    message: 'Demostrá tu valía en batalla y serás ascendido.',
    factionId: 'aresgard',
    factionKing: true,
  },
  npc_king_elvaria: {
    id: 'npc_king_elvaria',
    name: 'Rey de Elvaria',
    sprite: 'guardian',
    bodyId: 3,
    headId: 22,
    interact: 1,
    message: 'Los héroes de Elvaria son recompensados. ¿Cuántos enemigos caíste?',
    factionId: 'elvaria',
    factionKing: true,
  },

  // --- Guardias melee: hostiles solo contra enemigos de su facción ---
  npc_guard_melee_aresgard: {
    id: 'npc_guard_melee_aresgard',
    name: 'Guardia de Aresgard',
    sprite: 'guardian',
    bodyId: 1,
    headId: 43,
    weaponId: 1,
    shieldId: 1,
    hostile: true,
    attackable: true,
    normalViewport: true, // mismo rango de visión que el player
    maxHp: 800,
    minDamage: 25,
    maxDamage: 45,
    physDef: 30,
    magicDef: 20,
    evasion: 0.05,
    attackSpeed: 1.1,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 15000,
    factionId: 'aresgard',
    guardType: 'melee',
  },
  npc_guard_melee_elvaria: {
    id: 'npc_guard_melee_elvaria',
    name: 'Guardia de Elvaria',
    sprite: 'guardian',
    bodyId: 1,
    headId: 23,
    weaponId: 1,
    shieldId: 1,
    hostile: true,
    attackable: true,
    normalViewport: true,
    maxHp: 800,
    minDamage: 25,
    maxDamage: 45,
    physDef: 30,
    magicDef: 20,
    evasion: 0.05,
    attackSpeed: 1.1,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 15000,
    factionId: 'elvaria',
    guardType: 'melee',
  },

  // --- Guardias caster: lanzan spells a enemigos de su facción ---
  npc_guard_caster_aresgard: {
    id: 'npc_guard_caster_aresgard',
    name: 'Hechicero de Aresgard',
    sprite: 'mentor',
    bodyId: 3,
    headId: 44,
    weaponId: 0,
    hostile: true,
    attackable: true,
    normalViewport: true,
    maxHp: 600,
    minDamage: 10,
    maxDamage: 18,
    physDef: 15,
    magicDef: 35,
    evasion: 0.05,
    attackSpeed: 1.0,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 15000,
    factionId: 'aresgard',
    guardType: 'caster',
    npcSpells: [
      { spellId: 'spell_rayo_arcano', priority: 'on_target' },
      { spellId: 'spell_curar_heridas', priority: 'when_hp_below', hpThreshold: 0.35 },
    ],
  },
  npc_guard_caster_elvaria: {
    id: 'npc_guard_caster_elvaria',
    name: 'Hechicero de Elvaria',
    sprite: 'mentor',
    bodyId: 3,
    headId: 24,
    weaponId: 0,
    hostile: true,
    attackable: true,
    normalViewport: true,
    maxHp: 600,
    minDamage: 10,
    maxDamage: 18,
    physDef: 15,
    magicDef: 35,
    evasion: 0.05,
    attackSpeed: 1.0,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 15000,
    factionId: 'elvaria',
    guardType: 'caster',
    npcSpells: [
      { spellId: 'spell_rayo_arcano', priority: 'on_target' },
      { spellId: 'spell_curar_heridas', priority: 'when_hp_below', hpThreshold: 0.35 },
    ],
  },

  hostile_001: {
    id: 'hostile_001',
    kingdom: 'humanoid',
    name: 'Mob Hostil',
    bodyId: 2,
    hostile: true,
    attackable: true,
    // Combate
    maxHp: 50,
    minDamage: 3,
    maxDamage: 8,
    physDef: 2,
    magicDef: 1,
    evasion: 0.05,
    attackSpeed: 1.0,
    moveSpeed: 1.0,
    // Respawn
    respawn: true,
    respawnTime: 30000,
    notifyOnRespawn: false,
    isBoss: false,
    // XP
    expReward: 150,
    expOnHit: false,
    // Loot (rolls independientes). Pociones eliminadas en wipe 2026-05-25.
    lootTable: [
      { itemId: 'weapon_sword_001', chance: 0.02, minQty: 1, maxQty: 1 },
    ],
    // Spells (a usar por el AI en Etapa 4)
    npcSpells: [
      { spellId: 'spell_curar_heridas', priority: 'when_hp_below', hpThreshold: 0.3 },
      { spellId: 'spell_rayo_arcano', priority: 'on_target' },
    ],
  },

  // =========================================================================
  // MOBS CON SPRITE PROPIO (bodies 201-204, assets/entities/npcs/npc1-4.png).
  // Lobos + Lord Orco (boss). Cableados a quest_intro_hunt para testear. Stats placeholder.
  // =========================================================================
  hostile_wolf_gray: {
    id: 'hostile_wolf_gray',
    kingdom: 'animal',
    name: 'Lobo Gris',
    bodyId: 201,
    hostile: true,
    attackable: true,
    maxHp: 40,
    minDamage: 3,
    maxDamage: 7,
    physDef: 1,
    evasion: 0.05,
    attackSpeed: 0.75, // 1/4 más lento al pegar (base 1.0)
    moveSpeed: 0.5, // mitad de la velocidad de movimiento (base 1.0)
    respawn: true,
    respawnTime: 20000,
    expReward: 80,
    goldReward: { min: 4, max: 12 },
  },
  hostile_wolf_golden: {
    id: 'hostile_wolf_golden',
    kingdom: 'animal',
    name: 'Lobo Dorado',
    bodyId: 202,
    hostile: true,
    attackable: true,
    maxHp: 70,
    minDamage: 5,
    maxDamage: 10,
    physDef: 2,
    evasion: 0.06,
    attackSpeed: 0.825, // 1/4 más lento al pegar (base 1.1)
    moveSpeed: 0.525, // mitad de la velocidad de movimiento (base 1.05)
    respawn: true,
    respawnTime: 30000,
    expReward: 140,
    goldReward: { min: 10, max: 28 },
    lootTable: [{ itemId: 'weapon_sword_001', chance: 0.05, minQty: 1, maxQty: 1 }],
  },
  hostile_wolf_bloody: {
    id: 'hostile_wolf_bloody',
    kingdom: 'animal',
    name: 'Lobo Sangriento',
    bodyId: 203,
    hostile: true,
    attackable: true,
    maxHp: 110,
    minDamage: 8,
    maxDamage: 14,
    physDef: 3,
    magicDef: 1,
    evasion: 0.07,
    attackSpeed: 0.8625, // 1/4 más lento al pegar (base 1.15)
    moveSpeed: 0.55, // mitad de la velocidad de movimiento (base 1.1)
    respawn: true,
    respawnTime: 45000,
    expReward: 240,
    goldReward: { min: 18, max: 40 },
    lootTable: [{ itemId: 'weapon_sword_001', chance: 0.08, minQty: 1, maxQty: 1 }],
  },
  hostile_orc_lord: {
    id: 'hostile_orc_lord',
    kingdom: 'humanoid',
    name: 'Lord Orco',
    bodyId: 204,
    hostile: true,
    attackable: true,
    isBoss: true,
    maxHp: 300,
    minDamage: 14,
    maxDamage: 24,
    physDef: 6,
    magicDef: 3,
    evasion: 0.05,
    attackSpeed: 0.75, // 1/4 más lento al pegar (base 1.0)
    moveSpeed: 0.475, // mitad de la velocidad de movimiento (base 0.95)
    respawn: true,
    respawnTime: 120000,
    notifyOnRespawn: true,
    expReward: 800,
    goldReward: { min: 80, max: 160 },
    lootTable: [{ itemId: 'weapon_sword_001', chance: 0.5, minQty: 1, maxQty: 1 }],
  },

  // =========================================================================
  // CRIATURAS ACUÁTICAS / ANFIBIAS (batch animals). Estrenan `traversal`:
  //  - 'water': sólo se mueven en agua (peces/tiburones/mantarraya/calamar).
  //  - 'both' : anfibias (cangrejos/tortuga). Default 'land' = tierra (el resto).
  // Ver docs/NPC_HOSTILE_AI.md §Traversal. bodyId 253-260 = npc53-60 (hostile/).
  // =========================================================================
  hostile_piranha: {
    id: 'hostile_piranha',
    kingdom: 'animal',
    name: 'Piraña',
    bodyId: 254,
    traversal: 'water',
    hostile: true,
    attackable: true,
    maxHp: 25,
    minDamage: 4,
    maxDamage: 8,
    evasion: 0.08,
    attackSpeed: 1.1,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 15000,
    expReward: 55,
    goldReward: { min: 2, max: 8 },
  },
  hostile_manta_ray: {
    id: 'hostile_manta_ray',
    kingdom: 'animal',
    name: 'Mantarraya',
    bodyId: 256,
    traversal: 'water',
    attackable: true,
    maxHp: 40,
    minDamage: 3,
    maxDamage: 6,
    evasion: 0.1,
    moveSpeed: 0.9,
    respawn: true,
    respawnTime: 25000,
    expReward: 70,
    goldReward: { min: 4, max: 12 },
  },
  hostile_shark_gray: {
    id: 'hostile_shark_gray',
    kingdom: 'animal',
    name: 'Tiburón Gris',
    bodyId: 258,
    traversal: 'water',
    hostile: true,
    attackable: true,
    maxHp: 90,
    minDamage: 8,
    maxDamage: 15,
    physDef: 2,
    evasion: 0.06,
    moveSpeed: 0.9,
    respawn: true,
    respawnTime: 35000,
    expReward: 160,
    goldReward: { min: 12, max: 30 },
    lootTable: [{ itemId: 'gem_aquamarine', chance: 0.04, minQty: 1, maxQty: 1 }],
  },
  hostile_shark_white: {
    id: 'hostile_shark_white',
    kingdom: 'animal',
    name: 'Tiburón Blanco',
    bodyId: 259,
    traversal: 'water',
    hostile: true,
    attackable: true,
    maxHp: 140,
    minDamage: 12,
    maxDamage: 22,
    physDef: 3,
    evasion: 0.06,
    moveSpeed: 0.95,
    respawn: true,
    respawnTime: 50000,
    expReward: 260,
    goldReward: { min: 24, max: 55 },
    lootTable: [{ itemId: 'gem_amber', chance: 0.05, minQty: 1, maxQty: 1 }],
  },
  hostile_kraken: {
    id: 'hostile_kraken',
    kingdom: 'animal',
    name: 'Kraken',
    bodyId: 255,
    traversal: 'water',
    hostile: true,
    attackable: true,
    isBoss: true,
    maxHp: 280,
    minDamage: 16,
    maxDamage: 28,
    physDef: 5,
    magicDef: 4,
    evasion: 0.04,
    attackSpeed: 0.8,
    moveSpeed: 0.7,
    respawn: true,
    respawnTime: 120000,
    notifyOnRespawn: true,
    expReward: 600,
    goldReward: { min: 60, max: 140 },
    lootTable: [{ itemId: 'gem_ruby', chance: 0.1, minQty: 1, maxQty: 1 }],
  },
  hostile_crab: {
    id: 'hostile_crab',
    kingdom: 'animal',
    name: 'Cangrejo',
    bodyId: 253,
    traversal: 'both',
    attackable: true,
    maxHp: 35,
    minDamage: 3,
    maxDamage: 6,
    physDef: 4,
    evasion: 0.02,
    attackSpeed: 0.8,
    moveSpeed: 0.6,
    respawn: true,
    respawnTime: 20000,
    expReward: 50,
    goldReward: { min: 3, max: 10 },
  },
  hostile_crab_red: {
    id: 'hostile_crab_red',
    kingdom: 'animal',
    name: 'Cangrejo Rojo',
    bodyId: 257,
    traversal: 'both',
    hostile: true,
    attackable: true,
    maxHp: 55,
    minDamage: 5,
    maxDamage: 10,
    physDef: 5,
    evasion: 0.03,
    attackSpeed: 0.8,
    moveSpeed: 0.65,
    respawn: true,
    respawnTime: 30000,
    expReward: 85,
    goldReward: { min: 6, max: 16 },
  },
  hostile_turtle: {
    id: 'hostile_turtle',
    kingdom: 'animal',
    name: 'Tortuga',
    bodyId: 260,
    traversal: 'both',
    attackable: true,
    maxHp: 90,
    minDamage: 2,
    maxDamage: 5,
    physDef: 8,
    magicDef: 3,
    evasion: 0.0,
    attackSpeed: 0.7,
    moveSpeed: 0.45,
    respawn: true,
    respawnTime: 30000,
    expReward: 95,
    goldReward: { min: 5, max: 14 },
  },

  // =========================================================================
  // HOSTILE TEMPLATES — set de prueba para verificar todas las flags.
  // Comparten bodyId: 2 pero cada uno demuestra una feature distinta:
  // básico, expOnHit, normalViewport corto, y boss con notify.
  // =========================================================================
  hostile_grande_basico: {
    id: 'hostile_grande_basico',
    kingdom: 'beast',
    name: 'Bestia Grande',
    bodyId: 2,
    hostile: true,
    attackable: true,
    maxHp: 80,
    minDamage: 5,
    maxDamage: 10,
    physDef: 3,
    magicDef: 2,
    evasion: 0.05,
    attackSpeed: 1.0,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 30000,
    expReward: 200,
    expOnHit: false,
    goldReward: { min: 8, max: 25 },
    lootTable: [
      { itemId: 'weapon_sword_001', chance: 0.05, minQty: 1, maxQty: 1 },
    ],
    npcSpells: [
      { spellId: 'spell_curar_heridas', priority: 'when_hp_below', hpThreshold: 0.4 },
      { spellId: 'spell_rayo_arcano', priority: 'on_target' },
    ],
  },

  hostile_grande_xp_hit: {
    id: 'hostile_grande_xp_hit',
    kingdom: 'beast',
    name: 'Bestia (XP por golpe)',
    bodyId: 2,
    hostile: true,
    attackable: true,
    maxHp: 150,
    minDamage: 4,
    maxDamage: 9,
    physDef: 2,
    magicDef: 2,
    evasion: 0.0,
    attackSpeed: 1.0,
    moveSpeed: 1.0,
    respawn: true,
    respawnTime: 30000,
    expReward: 300,        // se reparte por daño: cada hit otorga 300×dañoHit/150
    expOnHit: true,
    lootTable: [],
    npcSpells: [
      { spellId: 'spell_rayo_arcano', priority: 'on_target' },
    ],
  },

  hostile_grande_corto: {
    id: 'hostile_grande_corto',
    kingdom: 'beast',
    name: 'Bestia Miope',
    bodyId: 2,
    hostile: true,
    attackable: true,
    maxHp: 60,
    minDamage: 3,
    maxDamage: 7,
    physDef: 1,
    magicDef: 1,
    evasion: 0.10,
    attackSpeed: 1.2,
    moveSpeed: 1.0,
    normalViewport: true,  // solo te ve cuando vos lo ves en pantalla (rango 20×14)
    respawn: true,
    respawnTime: 20000,
    expReward: 120,
    lootTable: [],
    npcSpells: [], // solo melee
  },

  // Mob melee-only para testear pathfinding/chase sin que los spells
  // distorsionen el experimento. Stats bajos: aguanta varios golpes y pega
  // suave para que el player no muera mientras se ajusta el chase.
  hostile_melee_test: {
    id: 'hostile_melee_test',
    kingdom: 'beast',
    name: 'Bestia Melee (Test)',
    bodyId: 2,
    hostile: true,
    attackable: true,
    maxHp: 200,
    minDamage: 2,
    maxDamage: 4,
    physDef: 0,
    magicDef: 0,
    evasion: 0,
    attackSpeed: 0.6,    // ~1.67s por golpe melee → fácil de observar
    moveSpeed: 1.0,      // 1 tile / 200ms
    respawn: false,
    expReward: 0,
    lootTable: [],
    // SIN npcSpells — AI cae al fallback de melee adyacente.
  },

  hostile_grande_boss: {
    id: 'hostile_grande_boss',
    kingdom: 'beast',
    name: 'Jefe Bestial',
    bodyId: 2,
    helmId: 1,             // un casquito para diferenciarlo visualmente
    hostile: true,
    attackable: true,
    isBoss: true,
    maxHp: 400,
    minDamage: 8,
    maxDamage: 16,
    physDef: 6,
    magicDef: 5,
    evasion: 0.08,
    attackSpeed: 0.9,
    moveSpeed: 0.9,
    respawn: true,
    respawnTime: 60000,
    notifyOnRespawn: true, // broadcast a la sala al respawnear (Etapa 6)
    expReward: 1500,
    expOnHit: false,
    goldReward: { min: 200, max: 500 },
    lootTable: [
      { itemId: 'weapon_sword_001', chance: 0.25, minQty: 1, maxQty: 1 },
    ],
    npcSpells: [
      { spellId: 'spell_curar_heridas', priority: 'when_hp_below', hpThreshold: 0.5 },
      { spellId: 'spell_tormenta_fuego', priority: 'on_target', cooldownOverride: 8000 },
      { spellId: 'spell_rayo_arcano', priority: 'on_target' },
    ],
  },

  // =========================================================================
  // SUMMON NPC TEMPLATES — usados por las INVOCACIONES del jugador.
  // `hostile: true` para poder pegar; `attackable: true` para poder morir. La AI
  // de mob NO los procesa (los excluye `npc.ownerId`); su comportamiento lo
  // maneja SummonSystem/GameRoom. `expReward: 0`: matar un summon no da XP. El
  // daño usa estos valores + los bonos `summon*` del owner. Ver docs/SUMMONS.md.
  // =========================================================================
  summon_wolf: {
    id: 'summon_wolf',
    kingdom: 'animal',
    name: 'Lobo Espiritual',
    bodyId: 1,
    hostile: true,
    attackable: true,
    maxHp: 80,
    minDamage: 4,
    maxDamage: 9,
    attackSpeed: 1.1,
    moveSpeed: 1.3,
    respawnTime: 0,
    expReward: 0,
  },
  summon_skeleton: {
    id: 'summon_skeleton',
    kingdom: 'undead',
    name: 'Esqueleto Guerrero',
    bodyId: 1,
    hostile: true,
    attackable: true,
    maxHp: 50,
    minDamage: 3,
    maxDamage: 7,
    respawnTime: 0,
    expReward: 0,
  },
  summon_golem: {
    id: 'summon_golem',
    kingdom: 'construct',
    name: 'Golem de Piedra',
    bodyId: 2,
    hostile: true,
    attackable: true,
    maxHp: 250,
    minDamage: 8,
    maxDamage: 14,
    attackSpeed: 0.7,
    moveSpeed: 0.8,
    respawnTime: 0,
    expReward: 0,
  },
  // Guardián de piedra — invocado por amuleto equipado (grant, permanente).
  summon_guardian: {
    id: 'summon_guardian',
    kingdom: 'construct',
    name: 'Guardián de Piedra',
    bodyId: 2,
    hostile: true,
    attackable: true,
    maxHp: 180,
    minDamage: 7,
    maxDamage: 12,
    attackSpeed: 0.75,
    moveSpeed: 0.9,
    respawnTime: 0,
    expReward: 0,
  },
  // Sabueso de guerra — invocado por amuleto usable (cooldown, timed).
  summon_warhound: {
    id: 'summon_warhound',
    kingdom: 'animal',
    name: 'Sabueso de Guerra',
    bodyId: 1,
    hostile: true,
    attackable: true,
    maxHp: 90,
    minDamage: 6,
    maxDamage: 11,
    attackSpeed: 1.2,
    moveSpeed: 1.4,
    respawnTime: 0,
    expReward: 0,
  },
  // Diablillo — invocado por poción consumible (timed, corto).
  summon_imp: {
    id: 'summon_imp',
    kingdom: 'demon',
    name: 'Diablillo',
    bodyId: 1,
    hostile: true,
    attackable: true,
    maxHp: 45,
    minDamage: 5,
    maxDamage: 9,
    attackSpeed: 1.1,
    moveSpeed: 1.2,
    respawnTime: 0,
    expReward: 0,
  },
  // Araña — invocada por poción consumible (timed, varias a la vez).
  summon_spider: {
    id: 'summon_spider',
    kingdom: 'animal',
    name: 'Araña Invocada',
    bodyId: 1,
    hostile: true,
    attackable: true,
    maxHp: 35,
    minDamage: 3,
    maxDamage: 6,
    attackSpeed: 1.3,
    moveSpeed: 1.3,
    respawnTime: 0,
    expReward: 0,
  },

  // ─── SUMMONS DE SOPORTE (role support) — no atacan; asisten al owner. ───────
  // `hostile: false` (no pegan) pero `attackable: true` (se pueden matar). Su
  // comportamiento (castear sobre el owner) lo maneja SummonSystem/GameRoom. Ver
  // docs/SUMMONS.md.
  summon_healer: {
    id: 'summon_healer',
    kingdom: 'humanoid',
    name: 'Espíritu Sanador',
    bodyId: 1,
    headId: 1,
    hostile: false,
    attackable: true,
    maxHp: 60,
    moveSpeed: 1.2,
    respawnTime: 0,
    expReward: 0,
  },
  summon_liberator: {
    id: 'summon_liberator',
    kingdom: 'humanoid',
    name: 'Espíritu Liberador',
    bodyId: 1,
    headId: 1,
    hostile: false,
    attackable: true,
    maxHp: 55,
    moveSpeed: 1.3,
    respawnTime: 0,
    expReward: 0,
  },
  summon_bard: {
    id: 'summon_bard',
    kingdom: 'humanoid',
    name: 'Duende Bardo',
    bodyId: 1,
    headId: 1,
    hostile: false,
    attackable: true,
    maxHp: 55,
    moveSpeed: 1.2,
    respawnTime: 0,
    expReward: 0,
  },
  summon_manacarrier: {
    id: 'summon_manacarrier',
    kingdom: 'humanoid',
    name: 'Espíritu de Maná',
    bodyId: 1,
    headId: 1,
    hostile: false,
    attackable: true,
    maxHp: 60,
    moveSpeed: 1.2,
    respawnTime: 0,
    expReward: 0,
  },
  // Centinela Vidente (detector estático): moveSpeed 0 → no se mueve; castea el Pulso
  // Revelador seguido. 100 de vida. Ver docs/INVISIBILITY.md.
  summon_sentinel: {
    id: 'summon_sentinel',
    kingdom: 'humanoid',
    name: 'Centinela Vidente',
    bodyId: 1,
    headId: 1,
    hostile: false,
    attackable: true,
    maxHp: 100,
    moveSpeed: 0,
    respawnTime: 0,
    expReward: 0,
  },
  // Espíritu Vidente del Bosque (detector móvil): sigue al owner y castea el Pulso cada 2 min.
  summon_seer_spirit: {
    id: 'summon_seer_spirit',
    kingdom: 'humanoid',
    name: 'Espíritu Vidente del Bosque',
    bodyId: 1,
    headId: 1,
    hostile: false,
    attackable: true,
    maxHp: 80,
    moveSpeed: 1.2,
    respawnTime: 0,
    expReward: 0,
  },

  // ─── MOB que PRIORIZA invocaciones (test de prioritizeSummons). Testeable con
  //     /invocar hostile_summon_hunter. Ver docs/SUMMONS.md / NPC_HOSTILE_AI.md. ──
  hostile_summon_hunter: {
    id: 'hostile_summon_hunter',
    kingdom: 'demon',
    name: 'Cazador de Invocaciones',
    bodyId: 204,
    hostile: true,
    attackable: true,
    prioritizeSummons: true,
    maxHp: 140,
    minDamage: 8,
    maxDamage: 14,
    attackSpeed: 1.0,
    moveSpeed: 1.1,
    respawn: true,
    respawnTime: 30000,
    expReward: 200,
    goldReward: { min: 40, max: 90 },
  },
};

// ===========================================================================
// BESTIARIO IMPORTADO — criaturas de los bodies 205-252 (npc5-52 en hostile/).
// Eran sprites SIN criatura; acá se generan sus templates data-driven, agrupados
// por Reino (construct/undead/humanoid/beast/animal/dragon/elemental). Los stats
// salen de un tier temático; casi todos 'land'. Ver docs/CREATURE_KINGDOMS.md.
// ===========================================================================
type MobTier = 'critter' | 'weak' | 'normal' | 'strong' | 'elite' | 'boss';
const MOB_TIER_STATS: Record<
  MobTier,
  { hp: number; dmg: [number, number]; physDef: number; magicDef?: number; exp: number; gold: [number, number]; respawn: number; boss?: boolean }
> = {
  critter: { hp: 15, dmg: [1, 3], physDef: 0, exp: 25, gold: [1, 4], respawn: 12000 },
  weak: { hp: 30, dmg: [3, 6], physDef: 1, exp: 55, gold: [3, 10], respawn: 18000 },
  normal: { hp: 60, dmg: [5, 10], physDef: 2, exp: 100, gold: [8, 20], respawn: 25000 },
  strong: { hp: 110, dmg: [8, 16], physDef: 4, exp: 180, gold: [16, 38], respawn: 40000 },
  elite: { hp: 180, dmg: [12, 22], physDef: 6, magicDef: 3, exp: 300, gold: [30, 70], respawn: 60000 },
  boss: { hp: 350, dmg: [18, 30], physDef: 8, magicDef: 4, exp: 700, gold: [80, 160], respawn: 120000, boss: true },
};

interface ImportedCreature {
  bodyId: number;
  id: string; // hostile_<name> (inglés)
  name: string; // display ES
  kingdom: KingdomId;
  tier: MobTier;
  traversal?: TraversalType; // default land
  passive?: boolean; // hostile:false (presa: no agrede, sí es atacable)
}

/** Tabla del bestiario importado (bodyId 205-252). Orden = display. */
export const IMPORTED_CREATURES: ImportedCreature[] = [
  { bodyId: 205, id: 'hostile_cave_dragon', name: 'Dragón de Caverna', kingdom: 'dragon', tier: 'boss' },
  { bodyId: 206, id: 'hostile_cave_golem', name: 'Gólem de Caverna', kingdom: 'construct', tier: 'elite' },
  { bodyId: 207, id: 'hostile_cave_minispider', name: 'Araña de Caverna', kingdom: 'beast', tier: 'critter' },
  { bodyId: 208, id: 'hostile_cave_troll', name: 'Trol de Caverna', kingdom: 'humanoid', tier: 'strong' },
  { bodyId: 209, id: 'hostile_desert_creature', name: 'Bestia del Desierto', kingdom: 'beast', tier: 'normal' },
  { bodyId: 210, id: 'hostile_desert_mummy', name: 'Momia del Desierto', kingdom: 'undead', tier: 'strong' },
  { bodyId: 211, id: 'hostile_forest_chicken', name: 'Gallina Salvaje', kingdom: 'animal', tier: 'critter', passive: true },
  { bodyId: 212, id: 'hostile_forest_goblin', name: 'Goblin del Bosque', kingdom: 'humanoid', tier: 'weak' },
  { bodyId: 213, id: 'hostile_forest_gorilla', name: 'Gorila del Bosque', kingdom: 'beast', tier: 'strong' },
  { bodyId: 214, id: 'hostile_forest_pig', name: 'Jabalí', kingdom: 'animal', tier: 'weak', passive: true },
  { bodyId: 215, id: 'hostile_forest_plant', name: 'Planta Carnívora', kingdom: 'beast', tier: 'normal' },
  { bodyId: 216, id: 'hostile_forest_spider', name: 'Araña del Bosque', kingdom: 'beast', tier: 'weak' },
  { bodyId: 217, id: 'hostile_forest_spider2', name: 'Araña Venenosa', kingdom: 'beast', tier: 'weak' },
  { bodyId: 218, id: 'hostile_forest_spider3', name: 'Araña Gigante', kingdom: 'beast', tier: 'normal' },
  { bodyId: 219, id: 'hostile_forest_tiger', name: 'Tigre del Bosque', kingdom: 'beast', tier: 'strong' },
  { bodyId: 220, id: 'hostile_golem_moss', name: 'Gólem de Musgo', kingdom: 'construct', tier: 'normal' },
  { bodyId: 221, id: 'hostile_golem_gold', name: 'Gólem de Oro', kingdom: 'construct', tier: 'strong' },
  { bodyId: 222, id: 'hostile_golem_silver2', name: 'Gólem de Plata', kingdom: 'construct', tier: 'strong' },
  { bodyId: 223, id: 'hostile_golem_water2', name: 'Gólem de Agua', kingdom: 'elemental', tier: 'normal', traversal: 'both' },
  { bodyId: 224, id: 'hostile_golem_gold_great', name: 'Gran Gólem de Oro', kingdom: 'construct', tier: 'elite' },
  { bodyId: 225, id: 'hostile_golem_iron_great', name: 'Gran Gólem de Hierro', kingdom: 'construct', tier: 'elite' },
  { bodyId: 226, id: 'hostile_golem_water_great', name: 'Gran Gólem de Agua', kingdom: 'elemental', tier: 'strong', traversal: 'both' },
  { bodyId: 227, id: 'hostile_golem_fire', name: 'Gólem de Fuego', kingdom: 'elemental', tier: 'strong' },
  { bodyId: 228, id: 'hostile_golem_flames', name: 'Gólem de Llamas', kingdom: 'elemental', tier: 'strong' },
  { bodyId: 229, id: 'hostile_golem_ember', name: 'Gólem Ardiente', kingdom: 'elemental', tier: 'elite' },
  { bodyId: 230, id: 'hostile_golem_lava', name: 'Gólem de Lava', kingdom: 'elemental', tier: 'elite' },
  { bodyId: 231, id: 'hostile_golem_mud', name: 'Gólem de Lodo', kingdom: 'construct', tier: 'normal', traversal: 'both' },
  { bodyId: 232, id: 'hostile_golem_silver', name: 'Gólem Plateado', kingdom: 'construct', tier: 'strong' },
  { bodyId: 233, id: 'hostile_golem_water', name: 'Elemental de Agua', kingdom: 'elemental', tier: 'normal', traversal: 'both' },
  { bodyId: 234, id: 'hostile_golem_winged', name: 'Gólem Alado', kingdom: 'construct', tier: 'elite' },
  { bodyId: 235, id: 'hostile_graveyard_ghost', name: 'Fantasma', kingdom: 'undead', tier: 'normal' },
  { bodyId: 236, id: 'hostile_graveyard_shadow', name: 'Sombra', kingdom: 'undead', tier: 'strong' },
  { bodyId: 237, id: 'hostile_graveyard_zombie', name: 'Zombi', kingdom: 'undead', tier: 'weak' },
  { bodyId: 238, id: 'hostile_medusa', name: 'Medusa', kingdom: 'beast', tier: 'elite' },
  { bodyId: 239, id: 'hostile_ogre_general', name: 'General Ogro', kingdom: 'humanoid', tier: 'elite' },
  { bodyId: 240, id: 'hostile_ogre_mountain', name: 'Ogro de Montaña', kingdom: 'humanoid', tier: 'strong' },
  { bodyId: 241, id: 'hostile_ogre_warrior', name: 'Ogro Guerrero', kingdom: 'humanoid', tier: 'strong' },
  { bodyId: 242, id: 'hostile_orc_wizard', name: 'Hechicero Orco', kingdom: 'humanoid', tier: 'normal' },
  { bodyId: 243, id: 'hostile_skeleton_archer', name: 'Esqueleto Arquero', kingdom: 'undead', tier: 'normal' },
  { bodyId: 244, id: 'hostile_skeleton_mage', name: 'Esqueleto Mago', kingdom: 'undead', tier: 'normal' },
  { bodyId: 245, id: 'hostile_skeleton_warrior', name: 'Esqueleto Guerrero', kingdom: 'undead', tier: 'normal' },
  { bodyId: 246, id: 'hostile_snow_beast', name: 'Bestia de las Nieves', kingdom: 'beast', tier: 'elite' },
  { bodyId: 247, id: 'hostile_snow_golem', name: 'Gólem de Nieve', kingdom: 'construct', tier: 'strong' },
  { bodyId: 248, id: 'hostile_snow_gorilla', name: 'Gorila de las Nieves', kingdom: 'beast', tier: 'strong' },
  { bodyId: 249, id: 'hostile_snow_wolf', name: 'Lobo de las Nieves', kingdom: 'animal', tier: 'normal' },
  { bodyId: 250, id: 'hostile_snow_yeti', name: 'Yeti', kingdom: 'beast', tier: 'elite' },
  { bodyId: 251, id: 'hostile_tower_warrior', name: 'Guardián de la Torre', kingdom: 'humanoid', tier: 'strong' },
  { bodyId: 252, id: 'hostile_wild_feline', name: 'Felino Salvaje', kingdom: 'animal', tier: 'normal' },
  { bodyId: 261, id: 'hostile_bat', name: 'Murciélago', kingdom: 'beast', tier: 'critter' },
  { bodyId: 262, id: 'hostile_scorpion', name: 'Escorpión', kingdom: 'beast', tier: 'weak' },
];

for (const c of IMPORTED_CREATURES) {
  const s = MOB_TIER_STATS[c.tier];
  NPC_TEMPLATES[c.id] = {
    id: c.id,
    kingdom: c.kingdom,
    name: c.name,
    bodyId: c.bodyId,
    traversal: c.traversal,
    hostile: !c.passive,
    attackable: true,
    isBoss: s.boss,
    maxHp: s.hp,
    minDamage: s.dmg[0],
    maxDamage: s.dmg[1],
    physDef: s.physDef,
    magicDef: s.magicDef,
    attackSpeed: 0.8,
    moveSpeed: 0.55,
    respawn: true,
    respawnTime: s.respawn,
    notifyOnRespawn: s.boss,
    expReward: s.exp,
    goldReward: { min: s.gold[0], max: s.gold[1] },
  };
}

/**
 * Obtiene un template de NPC o Mob por id
 */
export function getNPCTemplate(templateId: string): NPCTemplateDefinition | undefined {
  return NPC_TEMPLATES[templateId];
}

/**
 * Obtiene todos los templates de NPCs y Mobs
 */
export function getAllNPCTemplates(): NPCTemplateDefinition[] {
  return Object.values(NPC_TEMPLATES);
}

// ============================================================================
// DISPLAY INFO (para el panel right-click — derivable desde templates shared)
// ============================================================================

import { getSpellDefinition } from './spells';
import { getItemTemplate } from './items';
import type { KingdomId } from './kingdoms';
import type { NpcSounds } from './sounds';

export interface NpcDisplaySpell {
  spellId: string;
  spellName: string;
  cooldownSeconds: number;
  priority: 'when_hp_below' | 'on_target' | 'periodic';
  hpThreshold?: number;
  periodMs?: number;
}

export interface NpcDisplayLoot {
  itemId: string;
  itemName: string;
  itemIcon: string;
  chance: number;     // 0..1
  minQty: number;
  maxQty: number;
}

export interface NpcDisplayInfo {
  npcId: string;       // ID de la instancia
  templateId: string;
  name: string;
  isBoss: boolean;
  expReward: number;
  expOnHit: boolean;
  maxHp: number;
  minDamage: number;
  maxDamage: number;
  physDef: number;
  magicDef: number;
  evasion: number;
  attackSpeed: number;
  moveSpeed: number;
  spells: NpcDisplaySpell[];
  loot: NpcDisplayLoot[];
}

/**
 * Arma la info displayable de un NPC SIN tocar el server: los templates ya
 * viven en shared, así que tanto cliente como server pueden derivar esto
 * con sólo el `templateId` (y la instancia npcId/name para identificar).
 *
 * Devuelve null si el template no existe.
 */
export function buildNpcDisplayInfo(
  npcId: string,
  templateId: string,
  name: string
): NpcDisplayInfo | null {
  const template = getNPCTemplate(templateId);
  if (!template) return null;

  const spells: NpcDisplaySpell[] = [];
  if (template.npcSpells) {
    for (const entry of template.npcSpells) {
      const spell = getSpellDefinition(entry.spellId);
      if (!spell) continue;
      const cooldownSeconds =
        entry.cooldownOverride !== undefined ? entry.cooldownOverride / 1000 : spell.cooldown;
      spells.push({
        spellId: entry.spellId,
        spellName: spell.nameEs ?? spell.name,
        cooldownSeconds,
        priority: entry.priority,
        hpThreshold: entry.hpThreshold,
        periodMs: entry.periodMs,
      });
    }
  }

  const loot: NpcDisplayLoot[] = [];
  if (template.lootTable) {
    for (const entry of template.lootTable) {
      const item = getItemTemplate(entry.itemId);
      loot.push({
        itemId: entry.itemId,
        itemName: item?.name ?? entry.itemId,
        itemIcon: item?.icon ?? '',
        chance: entry.chance,
        minQty: entry.minQty,
        maxQty: entry.maxQty,
      });
    }
  }

  return {
    npcId,
    templateId,
    name,
    isBoss: template.isBoss === true,
    expReward: template.expReward ?? 0,
    expOnHit: template.expOnHit === true,
    maxHp: template.maxHp ?? 0,
    minDamage: template.minDamage ?? 1,
    maxDamage: template.maxDamage ?? 5,
    physDef: template.physDef ?? 0,
    magicDef: template.magicDef ?? 0,
    evasion: template.evasion ?? 0,
    attackSpeed: template.attackSpeed ?? 1.0,
    moveSpeed: template.moveSpeed ?? 1.0,
    spells,
    loot,
  };
}

